import React, { useState } from 'react';
import { useGameStore } from '../../store/gameStore';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Image as ImageIcon, Eye } from 'lucide-react';

// `hideImage` drops the location image and its show/hide control entirely —
// used by the /game3d HUD, where the walkable room replaces the still. It
// defaults to false so every existing caller keeps the current behaviour.
export const CenterPanel = ({ hideImage = false }) => {
  const { narrativeText, currentImage, currentImageVantage2, selectedTheme } = useGameStore();
  const [imageVantage, setImageVantage] = useState(1);
  const [showImage, setShowImage] = useState(true);

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];
  
  const displayedImage = imageVantage === 1 ? currentImage : (currentImageVantage2 || currentImage);

  return (
    <div className="p-3 md:p-4 lg:p-6 space-y-3 md:space-y-4 lg:space-y-6">
      {!hideImage && showImage && displayedImage && (
        <Card className="bg-black/40 backdrop-blur-sm border-white/10 overflow-hidden">
          <div className="aspect-[3/2] relative">
            <img
              src={displayedImage}
              alt="Scene"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to theme-appropriate image if image fails to load
                e.target.src = 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1200&h=800&fit=crop';
              }}
            />
            <div className="absolute bottom-2 right-2 md:bottom-4 md:right-4 flex gap-1 md:gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="min-h-touch min-w-touch bg-white/90 backdrop-blur-sm hover:bg-white text-black font-medium shadow-lg border-2 border-white/50 text-xs md:text-sm px-2 md:px-3"
                onClick={() => setImageVantage(imageVantage === 1 ? 2 : 1)}
              >
                <Eye className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Vantage {imageVantage}</span>
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="min-h-touch min-w-touch bg-white/90 backdrop-blur-sm hover:bg-white text-black font-medium shadow-lg border-2 border-white/50"
                onClick={() => setShowImage(false)}
              >
                <ImageIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {!hideImage && !showImage && (
        <Button
          variant="outline"
          className="w-full min-h-touch border-white/20 text-white hover:bg-white/10 text-sm md:text-base"
          onClick={() => setShowImage(true)}
        >
          <ImageIcon className="w-4 h-4 mr-2" />
          Show Scene Image
        </Button>
      )}

      <Card className="bg-black/40 backdrop-blur-sm border-white/10 p-3 md:p-4 lg:p-6" data-section="narrative">
        <div className="prose prose-invert max-w-none">
          <div className="text-gray-200 leading-relaxed space-y-3 md:space-y-4 text-sm md:text-base">
            {narrativeText ?
              narrativeText.split('\n\n').map((paragraph, idx) => {
                // Check if it's dialogue (contains quotes)
                if (paragraph.includes('"')) {
                  return (
                    <p key={idx} className="text-gray-100 italic pl-3 md:pl-4 border-l-2 border-white/20">
                      {paragraph}
                    </p>
                  );
                }
                // Check if it's an observation (contains asterisks or starts with italics marker)
                else if (paragraph.includes('*')) {
                  return (
                    <p key={idx} className="text-cyan-200">
                      {paragraph.replace(/\*/g, '')}
                    </p>
                  );
                }
                // Regular narration
                else {
                  return <p key={idx}>{paragraph}</p>;
                }
              })
              : 'The investigation begins...'
            }
          </div>
        </div>
      </Card>

      <Card className="bg-black/30 backdrop-blur-sm border-white/20 p-3 md:p-4">
        <div className="flex items-center gap-2 text-gray-400 text-xs md:text-sm">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: themeAccent }}></div>
          <span>Choose an action to continue your investigation...</span>
        </div>
      </Card>
    </div>
  );
};